import { useMemo } from 'react';
import { Chip } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  BusinessRounded as BusinessIcon,
  EcoRounded as EcoIcon,
  RoomRounded as RoomIcon,
  HomeWorkRounded as HomeWorkIcon,
} from '@material-ui/icons';
import { DELIVERY_POINT_TYPE, DeliveryPointType } from '../../types/delivery-point.type';
import { DELIVERY_POINT_TYPE_LABELS } from '../../constants/delivery-point-type.constants';
import { RELAY_CHIP_BACKGROUND, getRelayChipTextColor } from '../../../shared/components/Chips/relay-chip.styles';

type DeliveryPointTypeChipProps = {
  type: DeliveryPointType;
  size?: 'small' | 'medium';
};

const useStyles = makeStyles(() => ({
  root: (props: { backgroundColor: string; color: string }) => ({
    borderRadius: 4,
    textTransform: 'capitalize',
    backgroundColor: props.backgroundColor,
    color: props.color,

    '& .MuiChip-label': {
      display: 'flex',
      alignItems: 'center',
      gap: 6,

      '& > svg': {
        width: 16,
        height: 16,
        fill: props.color,
      },

      '& > span': {
        fontSize: 12,
        lineHeight: '14px',
      },
    },
  }),
}));

const getChipStyle = (
  type: DeliveryPointType,
  relayTextColor: string
): { backgroundColor: string; color: string; icon: React.ReactElement } => {
  switch (type) {
    case DELIVERY_POINT_TYPE.RELAY:
      return { backgroundColor: RELAY_CHIP_BACKGROUND, color: relayTextColor, icon: <RoomIcon /> };
    case DELIVERY_POINT_TYPE.ECO:
      return { backgroundColor: '#4caf50', color: '#ffffff', icon: <EcoIcon /> };
    case DELIVERY_POINT_TYPE.ENTREPRISE:
      return { backgroundColor: '#2196f3', color: '#ffffff', icon: <BusinessIcon /> };
    case DELIVERY_POINT_TYPE.CONCIERGERIE:
      return { backgroundColor: '#4caf50', color: '#ffffff', icon: <HomeWorkIcon /> };
  }
};

export default function DeliveryPointTypeChip({ type, size = 'small' }: DeliveryPointTypeChipProps) {
  const relayTextColor = getRelayChipTextColor(useTheme());
  const { label, backgroundColor, color, icon } = useMemo(() => {
    return {
      label: DELIVERY_POINT_TYPE_LABELS[type],
      ...getChipStyle(type, relayTextColor),
    };
  }, [type, relayTextColor]);

  const classes = useStyles({ backgroundColor, color });

  return (
    <Chip
      className={classes.root}
      size={size}
      label={
        <>
          {icon}
          <span>{label}</span>
        </>
      }
    />
  );
}
